import React, { useState, useCallback, useMemo, Fragment } from 'react';
import { X, Search, RotateCcw, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import {
  PL_COLLECTED_REVENUE_MARK,
  PL_COLLECTED_REVENUE_NOTE,
  ProjectPLRow as ProjectPLRowData,
} from '../../utils/projectPL';
import ProjectPLReadOnlyDetail from './ProjectPLReadOnlyDetail';

interface CompletedProjectsModalProps {
  open: boolean;
  rows: ProjectPLRowData[];
  formatCurrencyFull: (n: number) => string;
  onClose: () => void;
  onReopen: (dealId: string) => Promise<void>;
  onReinclude?: (dealId: string) => Promise<void>;
}

const CompletedProjectsModal: React.FC<CompletedProjectsModalProps> = ({
  open,
  rows,
  formatCurrencyFull,
  onClose,
  onReopen,
  onReinclude,
}) => {
  const [search, setSearch] = useState('');
  const [reopeningId, setReopeningId] = useState<string | null>(null);
  const [reincludingId, setReincludingId] = useState<string | null>(null);
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.dealTitle.toLowerCase().includes(q) || r.contactName.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const handleReopen = useCallback(
    async (dealId: string) => {
      setReopeningId(dealId);
      try {
        await onReopen(dealId);
      } finally {
        setReopeningId(null);
      }
    },
    [onReopen],
  );

  const handleReinclude = useCallback(
    async (dealId: string) => {
      if (!onReinclude) return;
      setReincludingId(dealId);
      try {
        await onReinclude(dealId);
      } finally {
        setReincludingId(null);
      }
    },
    [onReinclude],
  );

  const toggleExpanded = useCallback((dealId: string) => {
    setExpandedDealId(prev => (prev === dealId ? null : dealId));
  }, []);

  if (!open) return null;

  const totals = filtered.reduce(
    (acc, r) => {
      acc.revenue += r.revenue;
      acc.cost += r.actualCost;
      acc.margin += r.grossMargin;
      return acc;
    },
    { revenue: 0, cost: 0, margin: 0 },
  );

  return (
    <div className="sa-ppl-modal-backdrop" onClick={onClose}>
      <div
        className="sa-ppl-modal sa-ppl-modal-completed"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Completed projects"
      >
        <div className="sa-ppl-modal-head">
          <div>
            <h3>Completed projects</h3>
            <p className="sa-ppl-modal-sub">
              Read-only archive of finalized projects. Expand a row for transactions and cash flow, or reopen to move it back into the active P&L list.
            </p>
          </div>
          <button type="button" className="sa-ppl-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="sa-ppl-modal-toolbar">
          <div className="sa-ppl-modal-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search by project or client…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="sa-ppl-modal-summary">
            <span><strong>{filtered.length}</strong> project{filtered.length === 1 ? '' : 's'}</span>
            <span>Revenue <strong>{formatCurrencyFull(totals.revenue)}</strong></span>
            <span>Cost <strong>{formatCurrencyFull(totals.cost)}</strong></span>
            <span>Margin <strong>{formatCurrencyFull(totals.margin)}</strong></span>
          </div>
        </div>

        <div className="sa-ppl-modal-body">
          {filtered.length === 0 ? (
            <div className="sa-ppl-empty">No finalized projects yet.</div>
          ) : (
            <table className="sa-ppl-completed-table">
              <thead>
                <tr>
                  <th className="sa-ppl-completed-expand-col" aria-label="Expand" />
                  <th>Project</th>
                  <th>Finalized</th>
                  <th className="sa-ppl-num">Revenue</th>
                  <th className="sa-ppl-num">Actual cost</th>
                  <th className="sa-ppl-num">Margin</th>
                  <th className="sa-ppl-num">Variance</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const expanded = expandedDealId === r.dealId;
                  return (
                    <Fragment key={r.dealId}>
                      <tr className={expanded ? 'sa-ppl-completed-row-expanded' : undefined}>
                        <td className="sa-ppl-completed-expand-col">
                          <button
                            type="button"
                            className="sa-ppl-completed-expand-btn"
                            onClick={() => toggleExpanded(r.dealId)}
                            aria-expanded={expanded}
                            aria-label={expanded ? `Collapse ${r.dealTitle}` : `Expand ${r.dealTitle}`}
                          >
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="sa-ppl-completed-project-btn"
                            onClick={() => toggleExpanded(r.dealId)}
                          >
                            <div className="sa-ppl-row-title">{r.dealTitle}</div>
                            <div className="sa-ppl-row-sub">
                              {r.contactName}
                              {r.excluded ? ' · Excluded from P&L' : ''}
                            </div>
                          </button>
                        </td>
                        <td>{r.pmFinalizedAt ? r.pmFinalizedAt.toLocaleDateString() : '—'}</td>
                        <td className="sa-ppl-num" title={r.revenueSource === 'collected' ? PL_COLLECTED_REVENUE_NOTE : undefined}>
                          {formatCurrencyFull(r.revenue)}
                          {r.revenueSource === 'collected' && <span className="sa-ppl-collected-mark">{PL_COLLECTED_REVENUE_MARK}</span>}
                        </td>
                        <td className="sa-ppl-num">{formatCurrencyFull(r.actualCost)}</td>
                        <td className="sa-ppl-num">
                          {formatCurrencyFull(r.grossMargin)}{' '}
                          <span className="sa-ppl-row-sub">({r.grossMarginPct.toFixed(0)}%)</span>
                        </td>
                        <td className="sa-ppl-num">
                          {r.budget > 0
                            ? `${r.varianceVsBudget >= 0 ? '+' : ''}${r.varianceVsBudgetPct.toFixed(0)}%`
                            : '—'}
                        </td>
                        <td>
                          <div className="sa-ppl-completed-actions">
                            {r.excluded && onReinclude && (
                              <button
                                type="button"
                                className="sa-ppl-btn sa-ppl-btn-secondary sa-ppl-btn-sm"
                                disabled={reincludingId === r.dealId}
                                onClick={() => handleReinclude(r.dealId)}
                                title="Put this project back in P&L totals"
                              >
                                <Eye size={12} /> {reincludingId === r.dealId ? 'Re-including…' : 'Re-include'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="sa-ppl-btn sa-ppl-btn-secondary sa-ppl-btn-sm"
                              disabled={reopeningId === r.dealId}
                              onClick={() => handleReopen(r.dealId)}
                            >
                              <RotateCcw size={12} /> {reopeningId === r.dealId ? 'Reopening…' : 'Reopen'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="sa-ppl-completed-detail-row">
                          <td colSpan={8}>
                            <ProjectPLReadOnlyDetail row={r} formatCurrencyFull={formatCurrencyFull} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompletedProjectsModal;
